import React, { useEffect, useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { Card, Row, Col, Table, Button, Form, Badge, Modal, Dropdown } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

const STATUS_COLORS = { 0: "secondary", 1: "success", 3: "warning" };

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

const StudentAvatar = ({ photo, name }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default function ManageIslamicStudents() {
  const { ir_id } = useParams();
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const STATUS_BADGE = t.manageStudents.statusBadge;

  const [students, setStudents]       = useState([]);
  const [searchId, setSearchId]       = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchDone, setSearchDone]   = useState(false);
  const [yearList, setYearList]       = useState([]);
  const [rooms, setRooms]             = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [yearId, setYearId]           = useState(localStorage.getItem("yearId") || "");
  const [filterClass, setFilterClass]   = useState("");
  const [filterGender, setFilterGender] = useState("");

  const fetchStudents = useCallback(() => {
    if (!ir_id) return;
    setLoading(true);
    axiosInstance.get(`/api/islamic/getStudentsInRoom/${ir_id}`)
      .then(r => setStudents(r.data))
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  }, [ir_id, setLoading]);

  useEffect(() => {
    axiosInstance.get("/api/getStudyYear").then(r => {
      setYearList(r.data);
      if (!yearId && r.data.length > 0) {
        const id = String(r.data[0].year_id);
        localStorage.setItem("yearId", id);
        setYearId(id);
      }
    });
  }, []);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`/api/islamic/getRooms/${yearId}`)
      .then(r => {
        setRooms(r.data);
        const room = r.data.find(rm => String(rm.ir_id) === String(ir_id));
        setCurrentRoom(room || null);
      })
      .catch(() => {});
  }, [yearId, ir_id]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const handleSearch = async () => {
    if (!searchId.trim()) return;
    setSearchDone(false);
    try {
      const { data } = await axiosInstance.get(`/api/islamic/findStudentByIdNumber/${searchId.trim()}`);
      setSearchResult(data);
    } catch {
      setSearchResult(null);
    }
    setSearchDone(true);
  };

  const handleEnroll = async () => {
    if (!searchResult) return;
    try {
      await axiosInstance.post("/api/islamic/enrollStudent", { tb_student_st_id: searchResult.st_id, ir_id });
      Swal.fire(t.manageStudents.successTitle, t.manageStudents.enrolledSuccess, "success");
      setSearchId(""); setSearchResult(null); setSearchDone(false);
      fetchStudents();
    } catch (err) {
      Swal.fire(t.manageStudents.errorTitle, err.response?.data?.message || t.common.error, "error");
    }
  };

  const handleUnenroll = (student) => {
    Swal.fire({
      title: t.manageStudents.confirmUnenroll(student.st_name),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t.manageStudents.confirmUnenrollBtn,
      cancelButtonText: t.manageStudents.cancelBtn,
    }).then(r => {
      if (!r.isConfirmed) return;
      axiosInstance.post("/api/islamic/unenrollStudent", { is_study_id: student.is_study_id })
        .then(() => { Swal.fire(t.manageStudents.successTitle, t.manageStudents.unenrolledSuccess, "success"); fetchStudents(); })
        .catch(err => Swal.fire(t.manageStudents.errorTitle, err.response?.data?.message || t.common.error, "error"));
    });
  };

  // ── Transfer room ─────────────────────────────────────────────────────────
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferStudyId, setTransferStudyId] = useState(null);
  const [transferStudentName, setTransferStudentName] = useState('');
  const [selectedTransferRoomId, setSelectedTransferRoomId] = useState('');

  const handleOpenTransferModal = (is_study_id, st_name) => {
    setTransferStudyId(is_study_id);
    setTransferStudentName(st_name);
    setSelectedTransferRoomId('');
    setShowTransferModal(true);
  };

  const handleSetTransfer = async () => {
    if (!selectedTransferRoomId) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกห้องปลายทาง' });
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post('/api/islamic/setStudentTransfer', {
        is_study_id: transferStudyId,
        transfer_room_id: Number(selectedTransferRoomId),
      });
      setShowTransferModal(false);
      fetchStudents();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกการย้ายได้' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = (st_id, newStatus) => {
    const isWithdraw = newStatus === '0';
    Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการเปลี่ยนสถานะ',
      html: 'การดำเนินการนี้จะมีผลกับทั้ง<strong>ภาคสามัญและภาคศาสนา</strong>'
        + (isWithdraw ? '<br/><span class="text-danger">เมื่อจำหน่ายแล้ว ระบบจะไม่บันทึกการเช็คชื่อของนักเรียนคนนี้</span>' : ''),
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: t.manageStudents.cancelBtn,
      confirmButtonColor: isWithdraw ? '#dc3545' : undefined,
    }).then(result => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.post('/api/updateStudentStatus', { student_id: st_id, st_status: newStatus })
        .then(() => {
          setStudents(prev => prev.map(s => s.st_id === st_id ? { ...s, st_status: newStatus } : s));
        })
        .catch(err => Swal.fire(t.manageStudents.errorTitle, err.response?.data?.message || t.common.error, "error"))
        .finally(() => setLoading(false));
    });
  };

  const handleCancelTransfer = async (is_study_id) => {
    setLoading(true);
    try {
      await axiosInstance.post('/api/islamic/setStudentTransfer', {
        is_study_id,
        transfer_room_id: null,
      });
      fetchStudents();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถยกเลิกการย้ายได้' });
    } finally {
      setLoading(false);
    }
  };

  // ── Import from Previous Year ─────────────────────────────────────────────
  const [showImportModal, setShowImportModal] = useState(false);
  const [importYearId, setImportYearId] = useState(null);
  const [importYearLabel, setImportYearLabel] = useState('');
  const [importRoomList, setImportRoomList] = useState([]);
  const [importRoomId, setImportRoomId] = useState(null);
  const [importRoomLabel, setImportRoomLabel] = useState('');
  const [importStudents, setImportStudents] = useState([]);

  const handleOpenImportModal = () => {
    setImportYearId(null); setImportYearLabel('');
    setImportRoomList([]); setImportRoomId(null); setImportRoomLabel('');
    setImportStudents([]);
    setShowImportModal(true);
  };

  const handleSelectImportYear = (id, year_year) => {
    setImportYearId(id);
    setImportYearLabel(`ปีการศึกษา ${year_year}`);
    setImportRoomId(null); setImportRoomLabel(''); setImportStudents([]);
    axiosInstance.get(`/api/islamic/getRooms/${id}`)
      .then(res => setImportRoomList(res.data))
      .catch(() => setImportRoomList([]));
  };

  const handleSelectImportRoom = (id, label) => {
    setImportRoomId(id); setImportRoomLabel(label); setImportStudents([]);
    axiosInstance.get(`/api/islamic/getStudentsInRoom/${id}`)
      .then(res => setImportStudents((res.data || []).map(s => ({ ...s, selected: true }))))
      .catch(() => setImportStudents([]));
  };

  const handleToggleImportStudent = (st_id) => {
    setImportStudents(prev => prev.map(s => s.st_id === st_id ? { ...s, selected: !s.selected } : s));
  };

  const handleToggleAllImport = (checked) => {
    setImportStudents(prev => prev.map(s => ({ ...s, selected: checked })));
  };

  const handleBulkImport = async () => {
    const selected = importStudents.filter(s => s.selected).map(s => s.st_id);
    if (selected.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกนักเรียน', text: 'กรุณาเลือกนักเรียนอย่างน้อย 1 คน' });
      return;
    }
    setLoading(true);
    try {
      const { data: result } = await axiosInstance.post('/api/islamic/bulkEnrollStudents', { stIds: selected, ir_id });
      setShowImportModal(false);
      fetchStudents();
      Swal.fire({ icon: 'success', title: 'นำเข้าสำเร็จ', text: `เพิ่มนักเรียน ${result.inserted} คน`, timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถนำเข้านักเรียนได้' });
    } finally {
      setLoading(false);
    }
  };

  // ── Excel Import ──────────────────────────────────────────────────────────
  const excelInputRef = useRef(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelFileName, setExcelFileName] = useState('');
  const [excelMatched, setExcelMatched] = useState([]);
  const [excelNotFound, setExcelNotFound] = useState([]);

  const ID_KEYWORDS     = ['เลขประจำตัว', 'เลขประจําตัว', 'รหัสนักเรียน', 'รหัสประจำตัว', 'st_idnumber', 'idnumber', 'รหัส'];
  const ARABIC_KEYWORDS = ['ชื่ออาหรับ', 'ชื่อภาษาอาหรับ', 'อาหรับ', 'st_arabicname', 'arabicname', 'arabname'];

  const extractRowsFromWorksheet = (worksheet) => {
    const aoa = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false, raw: false, defval: '' });
    if (aoa.length === 0) return [];
    const norm = (s) => String(s).toLowerCase().replace(/\s/g, '');

    let headerRow = -1, idCol = -1, arabicCol = -1;
    for (const t of ID_KEYWORDS) {
      const kw = norm(t);
      for (let r = 0; r < aoa.length && headerRow === -1; r++) {
        const row = aoa[r] || [];
        for (let c = 0; c < row.length; c++) {
          if (norm(row[c]).includes(kw)) { headerRow = r; idCol = c; break; }
        }
      }
      if (headerRow !== -1) break;
    }
    if (headerRow !== -1) {
      const hrow = aoa[headerRow] || [];
      for (const t of ARABIC_KEYWORDS) {
        const kw = norm(t);
        for (let c = 0; c < hrow.length; c++) {
          if (c !== idCol && norm(hrow[c]).includes(kw)) { arabicCol = c; break; }
        }
        if (arabicCol !== -1) break;
      }
    }
    const dataRows = headerRow !== -1 ? aoa.slice(headerRow + 1) : aoa;
    const col = idCol !== -1 ? idCol : 0;
    return dataRows
      .map(row => ({
        st_idnumber: String(row?.[col] ?? '').trim().replace(/[,\s]/g, ''),
        arabicName:  arabicCol !== -1 ? String(row?.[arabicCol] ?? '').trim() : '',
      }))
      .filter(r => r.st_idnumber);
  };

  const handlePickExcel = () => excelInputRef.current?.click();

  const resolveSheetAndPreview = async (worksheet, label) => {
    const rows = extractRowsFromWorksheet(worksheet);
    if (rows.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ไม่พบเลขประจำตัวในแท็บนี้', text: 'ตรวจสอบว่าแท็บที่เลือกมีคอลัมน์เลขประจำตัวนักเรียน' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosInstance.post('/api/islamic/resolveStudentsFromExcel', { rows });
      setExcelFileName(label);
      setExcelMatched((data.matched || []).map(s => ({ ...s, selected: true })));
      setExcelNotFound(data.notFound || []);
      setShowExcelModal(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถจับคู่นักเรียนได้' });
    } finally {
      setLoading(false);
    }
  };

  const handleExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let wb;
    try {
      wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    } catch {
      Swal.fire({ icon: 'error', title: 'อ่านไฟล์ไม่สำเร็จ', text: 'ไฟล์อาจไม่ใช่ Excel ที่ถูกต้องหรือเสียหาย' });
      return;
    }
    const sheetNames = wb.SheetNames || [];
    if (sheetNames.length === 0) { Swal.fire({ icon: 'warning', title: 'ไม่พบข้อมูลในไฟล์' }); return; }

    let sheetName = sheetNames[0];
    if (sheetNames.length > 1) {
      const { value } = await Swal.fire({
        title: 'เลือกแท็บที่จะนำเข้า',
        input: 'select',
        inputOptions: sheetNames.reduce((o, n) => { o[n] = n; return o; }, {}),
        inputValue: sheetName,
        showCancelButton: true,
        confirmButtonText: 'ถัดไป',
        cancelButtonText: 'ยกเลิก',
      });
      if (!value) return;
      sheetName = value;
    }
    await resolveSheetAndPreview(wb.Sheets[sheetName], `${file.name} — ${sheetName}`);
  };

  const handleToggleExcelStudent = (st_id) => {
    setExcelMatched(prev => prev.map(s => s.st_id === st_id ? { ...s, selected: !s.selected } : s));
  };

  const handleToggleAllExcel = (checked) => {
    setExcelMatched(prev => prev.map(s => ({ ...s, selected: checked })));
  };

  const handleBulkEnrollExcel = async () => {
    const selected = excelMatched.filter(s => s.selected).map(s => ({ st_id: s.st_id, arabicName: s.newArabicName }));
    if (selected.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกนักเรียน', text: 'กรุณาเลือกนักเรียนอย่างน้อย 1 คน' });
      return;
    }
    setLoading(true);
    try {
      const { data: result } = await axiosInstance.post('/api/islamic/bulkEnrollWithArabicName', { students: selected, ir_id });
      setShowExcelModal(false);
      fetchStudents();
      Swal.fire({
        icon: 'success', title: 'นำเข้าสำเร็จ',
        text: `เพิ่มนักเรียน ${result.inserted} คน${result.arabicUpdated ? ` • อัปเดตชื่ออาหรับ ${result.arabicUpdated} คน` : ''}`,
        timer: 2500, showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถนำเข้านักเรียนได้' });
    } finally {
      setLoading(false);
    }
  };

  const classList   = [...new Set(rooms.map(r => r.ic_class).filter(Boolean))].sort();
  const genderList  = [...new Set(rooms.map(r => r.ir_gender).filter(Boolean))].sort();
  const filteredRooms = rooms.filter(r =>
    (!filterClass  || r.ic_class   === filterClass) &&
    (!filterGender || r.ir_gender  === filterGender)
  );

  const roomLabel = currentRoom ? `${currentRoom.ic_class}/${currentRoom.ir_no}` : `-`;

  return (
    <React.Fragment>
      <input type="file" ref={excelInputRef} accept=".xlsx,.xls" onChange={handleExcelFile} style={{ display: 'none' }} />
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">{t.manageStudents.breadHome}</Link></li>
            <li className="breadcrumb-item"><Link to="/islamic/manage-rooms">{t.manageStudents.breadRooms}</Link></li>
            <li className="breadcrumb-item active">{t.manageStudents.breadCurrent}</li>
          </ol>
          <h4 className="main-title mb-0">{t.manageStudents.pageTitle(roomLabel)}</h4>
        </div>
        <div className="d-flex gap-2 align-items-center mt-2 mt-sm-0 flex-wrap">
          <Form.Select size="sm" value={yearId} onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }} style={{ width: 120 }}>
            {yearList.map(y => <option key={y.year_id} value={y.year_id}>{t.common.studyYearShort(y.year_year)}</option>)}
          </Form.Select>
          <Form.Select size="sm" value={filterClass} onChange={e => setFilterClass(e.target.value)} style={{ width: 130 }}>
            <option value="">{t.manageStudents.filterAllClass}</option>
            {classList.map(c => <option key={c} value={c}>{c}</option>)}
          </Form.Select>
          <Form.Select size="sm" value={filterGender} onChange={e => setFilterGender(e.target.value)} style={{ width: 130 }}>
            <option value="">{t.manageStudents.filterAllGender}</option>
            {genderList.map(g => <option key={g} value={g}>{t.manageStudents.genderLabel(g)}</option>)}
          </Form.Select>
          <Form.Select size="sm" value={ir_id} onChange={e => window.location.href = `/islamic/manage-students/${e.target.value}`} style={{ width: 180 }}>
            <option value="0">{t.manageStudents.filterAllRoom}</option>
            {filteredRooms.map(r => <option key={r.ir_id} value={r.ir_id}>{r.ic_class}/{r.ir_no}</option>)}
          </Form.Select>
        </div>
      </div>

      {(!ir_id || ir_id === "0") ? (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">
            <i className="ri-arrow-up-line fs-3 d-block mb-2"></i>
            {t.manageStudents.selectRoom}
          </Card.Body>
        </Card>
      ) : (
      <Row className="g-3">
        <Col xl={4}>
          <Card className="card-one">
            <Card.Header><Card.Title as="h6" className="mb-0">{t.manageStudents.addStudentTitle}</Card.Title></Card.Header>
            <Card.Body>
              <Form.Label className="fw-medium">{t.manageStudents.labelStudentId}</Form.Label>
              <div className="d-flex gap-2 mb-3">
                <Form.Control
                  value={searchId}
                  onChange={e => { setSearchId(e.target.value); setSearchDone(false); setSearchResult(null); }}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  placeholder={t.manageStudents.searchPlaceholder}
                />
                <Button variant="outline-primary" onClick={handleSearch}><i className="ri-search-line"></i></Button>
              </div>

              {searchDone && !searchResult && (
                <div className="alert alert-warning fs-sm py-2">{t.manageStudents.notFound(searchId)}</div>
              )}

              {searchResult && (
                <div className="border rounded p-3 mb-3">
                  <div className="fw-semibold">{searchResult.st_name}</div>
                  <div className="text-secondary fs-sm">{t.manageStudents.colNo}: {searchResult.st_idnumber}</div>
                  <div className="text-secondary fs-sm">
                    {t.manageStudents.labelGender}: {t.manageStudents.genderLabel(searchResult.st_gen)} &nbsp;|&nbsp;
                    {t.manageStudents.labelStatus}: <Badge bg={STATUS_COLORS[searchResult.st_status] || "secondary"} className="fw-normal">{STATUS_BADGE[searchResult.st_status] || t.manageStudents.noInfo}</Badge>
                  </div>
                  <Button size="sm" variant="success" className="mt-2 w-100" onClick={handleEnroll}>
                    <i className="ri-user-add-line me-1"></i>{t.manageStudents.enrollBtn}
                  </Button>
                </div>
              )}

              <div className="text-secondary fs-xs">
                <i className="ri-information-line me-1"></i>
                {t.manageStudents.searchHint}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8}>
          <Card className="card-one">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title as="h6" className="mb-0">{t.manageStudents.listTitle(students.length)}</Card.Title>
              <div className="d-flex gap-2">
                <Button variant="outline-info" size="sm" onClick={handleOpenImportModal}>
                  <i className="ri-download-2-line me-1"></i> ปีก่อน
                </Button>
                <Button variant="outline-success" size="sm" onClick={handlePickExcel}>
                  <i className="ri-file-excel-2-line me-1"></i> นำเข้า Excel
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th>#</th>
                    <th>{t.manageStudents.colNo}</th>
                    <th>{t.manageStudents.colName}</th>
                    <th>{t.manageStudents.colArabicName}</th>
                    <th>{t.manageStudents.colGender}</th>
                    <th>{t.manageStudents.colStatus}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-secondary py-4">{t.manageStudents.noStudents}</td></tr>
                  )}
                  {students.map((s, i) => {
                    const statusLabel = STATUS_BADGE[s.st_status] || t.manageStudents.noInfo;
                    const statusColor = STATUS_COLORS[s.st_status] || "secondary";
                    return (
                      <tr key={s.is_study_id} className={s.transfer_room_id ? 'table-warning' : ''}>
                        <td className="text-secondary">{i + 1}</td>
                        <td className="fw-medium">{s.st_idnumber}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <StudentAvatar photo={s.st_photo} name={s.st_name} />
                            <span>
                              {s.st_name}
                              {s.transfer_room_id && (
                                <small className="text-muted ms-2">(ย้ายไป {s.transfer_class}/{s.transfer_room_no})</small>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="arabic-text">{s.st_arabicname || <span className="text-muted">-</span>}</td>
                        <td>{t.manageStudents.genderLabel(s.st_gen)}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle as="span" style={{ cursor: 'pointer' }} title={t.manageStudents.changeStatusTitle}>
                              <Badge bg={statusColor} className="fw-normal">{statusLabel}</Badge>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleUpdateStatus(s.st_id, '1')}>
                                <i className="ri-check-line text-success"></i> {STATUS_BADGE[1]}
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleUpdateStatus(s.st_id, '0')}>
                                <i className="ri-close-line text-secondary"></i> {STATUS_BADGE[0]}
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleUpdateStatus(s.st_id, '3')}>
                                <i className="ri-user-shared-line text-warning"></i> {STATUS_BADGE[3]}
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              as={Link}
                              to={`/islamic/update-student/${s.st_id}?ir_id=${ir_id}`}
                              title={t.manageStudents.editBtn}
                            >
                              <i className="ri-edit-line"></i>
                            </Button>
                            {s.transfer_room_id ? (
                              <Button size="sm" variant="outline-warning" onClick={() => handleCancelTransfer(s.is_study_id)} title="ยกเลิกย้ายห้อง">
                                <i className="ri-arrow-go-back-line"></i>
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline-info" onClick={() => handleOpenTransferModal(s.is_study_id, s.st_name)} title="ย้ายห้อง">
                                <i className="ri-swap-line"></i>
                              </Button>
                            )}
                            <Button size="sm" variant="outline-danger" onClick={() => handleUnenroll(s)} title={t.manageStudents.unenrollBtn || 'นำออก'}><i className="ri-user-unfollow-line"></i></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      )}

      {/* Transfer Modal */}
      <Modal show={showTransferModal} onHide={() => setShowTransferModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>ย้ายห้อง — {transferStudentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">เลือกห้องปลายทาง:</div>
          <select
            className="form-select"
            value={selectedTransferRoomId}
            onChange={e => setSelectedTransferRoomId(e.target.value)}
          >
            <option value="">-- เลือกห้องปลายทาง --</option>
            {rooms.filter(r => String(r.ir_id) !== String(ir_id)).map(r => (
              <option key={r.ir_id} value={r.ir_id}>
                {r.ic_class}/{r.ir_no}{r.ir_gender ? ` (${r.ir_gender === 'm' ? 'ชาย' : 'หญิง'})` : ''}
              </option>
            ))}
          </select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransferModal(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleSetTransfer}>บันทึก</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>ดึงนักเรียนจากปีก่อน</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-3 mb-3 flex-wrap">
            <div>
              <label className="form-label fw-semibold">เลือกปีการศึกษาต้นทาง</label>
              <div>
                <select
                  className="form-select form-select-sm"
                  value={importYearId || ''}
                  onChange={e => {
                    const y = yearList.find(y => String(y.year_id) === e.target.value);
                    if (y) handleSelectImportYear(y.year_id, y.year_year);
                  }}
                  style={{ width: 160 }}
                >
                  <option value="">เลือกปีการศึกษา</option>
                  {yearList.filter(y => String(y.year_id) !== String(yearId)).map(y => (
                    <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
                  ))}
                </select>
              </div>
            </div>
            {importYearId && (
              <div>
                <label className="form-label fw-semibold">เลือกห้องเรียนต้นทาง</label>
                <div>
                  <select
                    className="form-select form-select-sm"
                    value={importRoomId || ''}
                    onChange={e => {
                      const r = importRoomList.find(r => String(r.ir_id) === e.target.value);
                      if (r) handleSelectImportRoom(r.ir_id, `${r.ic_class}/${r.ir_no}`);
                    }}
                    style={{ width: 200 }}
                  >
                    <option value="">เลือกห้องเรียน</option>
                    {importRoomList.map(r => (
                      <option key={r.ir_id} value={r.ir_id}>
                        {r.ic_class}/{r.ir_no}{r.ir_gender ? ` (${r.ir_gender === 'm' ? 'ชาย' : 'หญิง'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {importStudents.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted fs-sm">พบ {importStudents.length} คน — เลือก {importStudents.filter(s => s.selected).length} คน</span>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => handleToggleAllImport(true)}>เลือกทั้งหมด</Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => handleToggleAllImport(false)}>ล้างการเลือก</Button>
                </div>
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                <Table responsive hover size="sm" className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 40 }} className="text-center">#</th>
                      <th style={{ width: 110 }} className="text-center">เลขประจำตัว</th>
                      <th>ชื่อ-สกุล</th>
                      <th>ชื่ออาหรับ</th>
                      <th style={{ width: 50 }} className="text-center">เลือก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importStudents.map((s, idx) => (
                      <tr key={s.st_id} className={!s.selected ? 'text-muted' : ''}>
                        <td className="text-center">{idx + 1}</td>
                        <td className="text-center">{s.st_idnumber}</td>
                        <td>{s.st_name}</td>
                        <td className="arabic-text">{s.st_arabicname || <span className="text-muted">-</span>}</td>
                        <td className="text-center">
                          <input type="checkbox" checked={s.selected} onChange={() => handleToggleImportStudent(s.st_id)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {importRoomId && importStudents.length === 0 && (
            <div className="text-center text-muted py-3">ไม่พบนักเรียนในห้องนี้</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>ยกเลิก</Button>
          <Button
            variant="success"
            onClick={handleBulkImport}
            disabled={importStudents.filter(s => s.selected).length === 0}
          >
            <i className="ri-download-2-line me-1"></i>
            นำเข้า {importStudents.filter(s => s.selected).length} คน
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showExcelModal} onHide={() => setShowExcelModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>นำเข้าจาก Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted fs-sm mb-3">
            <i className="ri-file-excel-2-line me-1"></i>{excelFileName}
            {' — '}จับคู่จากเลขประจำตัว: พบ <span className="text-success fw-semibold">{excelMatched.length}</span> คน
            {excelNotFound.length > 0 && <>, ไม่พบ <span className="text-danger fw-semibold">{excelNotFound.length}</span> รายการ</>}
          </div>

          {excelMatched.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted fs-sm">เลือกแล้ว {excelMatched.filter(s => s.selected).length} คน</span>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => handleToggleAllExcel(true)}>เลือกทั้งหมด</Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => handleToggleAllExcel(false)}>ล้างการเลือก</Button>
                </div>
              </div>
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                <Table responsive hover size="sm" className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 40 }} className="text-center">#</th>
                      <th style={{ width: 110 }} className="text-center">เลขประจำตัว</th>
                      <th>ชื่อ-สกุล</th>
                      <th>ชื่ออาหรับ (ปัจจุบัน)</th>
                      <th>ชื่ออาหรับ (จาก Excel)</th>
                      <th style={{ width: 50 }} className="text-center">เลือก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelMatched.map((s, idx) => (
                      <tr key={s.st_id} className={!s.selected ? 'text-muted' : ''}>
                        <td className="text-center">{idx + 1}</td>
                        <td className="text-center">{s.st_idnumber}</td>
                        <td>{s.st_name}</td>
                        <td className="arabic-text text-secondary">{s.currentArabicName || <span className="text-muted">-</span>}</td>
                        <td className="arabic-text">{s.newArabicName || <span className="text-muted">-</span>}</td>
                        <td className="text-center">
                          <input type="checkbox" checked={s.selected} onChange={() => handleToggleExcelStudent(s.st_id)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {excelNotFound.length > 0 && (
            <div className="alert alert-warning mt-3 mb-0 py-2">
              <div className="fw-semibold mb-1"><i className="ri-error-warning-line me-1"></i>ไม่พบเลขประจำตัวเหล่านี้ในระบบ</div>
              <div className="fs-sm" style={{ maxHeight: 100, overflowY: 'auto' }}>{excelNotFound.join(', ')}</div>
            </div>
          )}

          {excelMatched.length === 0 && (
            <div className="text-center text-muted py-3">ไม่พบนักเรียนที่ตรงกับเลขประจำตัวในไฟล์</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExcelModal(false)}>ยกเลิก</Button>
          <Button
            variant="success"
            onClick={handleBulkEnrollExcel}
            disabled={excelMatched.filter(s => s.selected).length === 0}
          >
            <i className="ri-file-excel-2-line me-1"></i>
            นำเข้า {excelMatched.filter(s => s.selected).length} คน
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
